import { Modal, ModalProps } from 'antd';
import { MsgType, SlateValue2MustacheV2 } from 'components/TemplateEditor/mustacheUtils';
import { Concept } from 'components/TemplateEditor/toolbarPlugins/ConceptMenu';
import { Formik } from 'formik';
import { useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import {
  StyledForm,
  getMustacheExecuteContextV2,
  getSlateExecuteContext,
  useGetMsgTypes,
} from 'views/Aide/Scenario/PolicyForm';
import BilingualExecuteContextsFieldV2, {
  validate as bilingualValidate,
} from 'views/Aide/Scenario/PolicyForm/BilingualExecuteContextsFieldV2';
import CancelTradeContentsField, {
  validate as cancelTradeValidate,
} from 'views/Aide/Scenario/PolicyForm/CancelTradeContentsField';
import { GPTReGeneratorBtn } from './GPTGenerator/GPTReGeneratorBtn';
import { EventSourceQuery } from '../hooks/useEventSource';
import { text2mustache } from '@knowledge/Activity/PolicyForm/share';
import { mergePartialValue } from 'views/Aide/Scenario/PolicyForm/ExecuteContextsFieldV2';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { ItemSelectProvider } from '@knowledge/Activity/ActivityForm/ItemSelectProvider';
import { stores2storeScopes } from '@knowledge/Activity/share';
import { StoreContextProvider } from 'views/Aide/Scenario/NewPolicy/StoreContext';
import { voucherSerializer } from 'components/TemplateEditor/mustacheUtils/serializer/itemSerializer';

type Value =
  | string
  | Marketing.ExecuteContext
  | Marketing.ExecuteContext[]
  | Required<Aide.Policy>['cancelTradeConfig'];

function isSingleContext(value: any): value is Marketing.ExecuteContext {
  return typeof value?.content === 'string' && typeof value?.translatedContent === 'string';
}

export type EditModalProps<T> = ModalProps & {
  value: T;
  onChange: (value: T) => void;
  onSbChange?: (value: T) => Promise<any>;
  scenarioType: Marketing.AllScenarioType;
  platform?: General.PlatformUpperCase;
  region: General.UpperCaseRegion;
  replyOption?: Marketing.ReplyOption;
  extraConcepts?: Concept[];
  path?: string;
  query: EventSourceQuery;
  isActivity?: boolean;
  items?: Pick<
    Knowledge.Activity.Item,
    'platform' | 'region' | 'storeId' | 'shopId' | 'spuId' | 'itemInternalId'
  >[];
};

function EditModal<T extends Value>(props: EditModalProps<T>) {
  const {
    value,
    onChange,
    onSbChange,
    scenarioType,
    platform,
    region,
    replyOption,
    extraConcepts,
    path,
    query,
    onCancel,
    isActivity,
    items,
    ...otherProps
  } = props;

  const { t } = useTranslate();

  const lang = useSystemLanguageValue();

  const copyTip = lang === 'zh-CN' ? '复制至发送内容' : undefined;

  const initialValues = useMemo(() => {
    if (typeof value === 'string') {
      return {
        knowledgeId: isActivity ? 1 : undefined,
        platform,
        storeScopes: items ? stores2storeScopes(items) : undefined,
        contentItemList: items?.map(
          ({ platform, region, storeId, shopId, spuId, itemInternalId }) => ({
            platform,
            region,
            internalStoreId: storeId,
            externalStoreId: shopId,
            externalSpuId: spuId,
            internalItemId: String(itemInternalId),
          }),
        ),
        bilingualExecuteContexts: [
          getSlateExecuteContext({ content: value, translatedContent: '' }),
        ],
      };
    }

    if (isSingleContext(value)) {
      return {
        bilingualExecuteContexts: [getSlateExecuteContext(value)],
      };
    }

    if (Array.isArray(value)) {
      return {
        bilingualExecuteContexts: value.map(getSlateExecuteContext),
      };
    }

    return {
      cancelTradeConfig: {
        ...(value as Required<Aide.Policy>['cancelTradeConfig']),
        contents: value.contents.map(({ executeContext, ...others }) => {
          return {
            ...others,
            executeContext: getSlateExecuteContext(executeContext),
          };
        }),
      },
    };
  }, [value, isActivity, items, platform]);

  const getMsgTypes = useGetMsgTypes();

  const msgTypes = useMemo<MsgType[]>(() => {
    return platform
      ? getMsgTypes(platform, scenarioType)
      : items?.length
      ? ['text', 'item']
      : ['text'];
  }, [getMsgTypes, platform, scenarioType, items]);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validate={({ bilingualExecuteContexts, cancelTradeConfig }) => {
        const errors = {};
        if (bilingualExecuteContexts) {
          bilingualValidate(
            errors,
            { bilingualExecuteContexts } as any,
            t,
            platform || false,
            scenarioType,
          );
        }
        if (cancelTradeConfig && platform) {
          cancelTradeValidate(errors, { cancelTradeConfig } as any, t, platform);
        }
        return errors;
      }}
      onSubmit={({ bilingualExecuteContexts, cancelTradeConfig }) => {
        if (bilingualExecuteContexts?.length) {
          if (typeof value === 'string') {
            // 优惠券卡片处理
            const voucher = voucherSerializer();
            const nextValue = `#mustache#${SlateValue2MustacheV2(
              bilingualExecuteContexts[0].content,
            )}${value.includes(voucher) ? voucher : ''}` as T;
            if (onSbChange) {
              return onSbChange(nextValue);
            }
            onChange(nextValue);
          } else if (isSingleContext(value)) {
            onChange(getMustacheExecuteContextV2(bilingualExecuteContexts[0]) as T);
          } else if (Array.isArray(value)) {
            onChange(bilingualExecuteContexts.map(getMustacheExecuteContextV2) as T);
          }
        } else if (cancelTradeConfig) {
          onChange({
            ...cancelTradeConfig,
            contents: cancelTradeConfig.contents.map(({ executeContext, ...others }) => ({
              ...others,
              executeContext: getMustacheExecuteContextV2(executeContext),
            })),
          } as T);
        }

        onCancel?.({} as any);

        return Promise.resolve();
      }}
    >
      {({ values, setValues, isSubmitting, handleSubmit }) => {
        const { bilingualExecuteContexts, cancelTradeConfig } = values;

        return (
          <Modal
            title={t('marketing.编辑')}
            width={1100}
            okButtonProps={{
              loading: isSubmitting,
              onClick: () => handleSubmit(),
            }}
            maskClosable={false}
            onCancel={onCancel}
            {...otherProps}
          >
            <StyledForm>
              <div className="!p-0 !mt-0">
                <h3>{t('reviewSetting.发送内容')}</h3>
                {bilingualExecuteContexts && (
                  <BilingualExecuteContextsFieldV2
                    scenarioType={scenarioType}
                    platform={platform}
                    region={region}
                    msgTypes={msgTypes}
                    replyOption={replyOption}
                    showMsgTypes={Boolean(platform) || Boolean(items?.length)}
                    allowDividing={Boolean(platform)}
                    supportPreview={false}
                    extraConcepts={extraConcepts}
                    copyTip={copyTip}
                    extra={(index) => (
                      <GPTReGeneratorBtn
                        path={path}
                        query={query}
                        onFinish={(text) =>
                          setValues((pre) =>
                            pre.bilingualExecuteContexts
                              ? {
                                  ...pre,
                                  bilingualExecuteContexts: [
                                    ...pre.bilingualExecuteContexts.slice(0, index),
                                    mergePartialValue(
                                      bilingualExecuteContexts[index],
                                      getSlateExecuteContext({
                                        content: text2mustache(text),
                                        translatedContent: '',
                                      }),
                                      'text',
                                    ),
                                    ...pre.bilingualExecuteContexts.slice(index + 1),
                                  ],
                                }
                              : pre,
                          )
                        }
                      />
                    )}
                  />
                )}
                {cancelTradeConfig && platform && (
                  <CancelTradeContentsField
                    scenarioType={scenarioType}
                    platform={platform}
                    region={region}
                    msgTypes={msgTypes}
                    supportPreview={false}
                    extraConcepts={extraConcepts}
                    copyTip={copyTip}
                    extra={(sendPoint) => (
                      <GPTReGeneratorBtn
                        path={path}
                        query={query}
                        onFinish={(text) =>
                          setValues((pre) =>
                            pre.cancelTradeConfig
                              ? {
                                  ...pre,
                                  cancelTradeConfig: {
                                    ...pre.cancelTradeConfig,
                                    contents: pre.cancelTradeConfig.contents.map((content) =>
                                      content.sendPoint === sendPoint
                                        ? {
                                            ...content,
                                            executeContext: mergePartialValue(
                                              content.executeContext,
                                              getSlateExecuteContext({
                                                content: text2mustache(text),
                                                translatedContent: '',
                                              }),
                                              'text',
                                            ),
                                          }
                                        : content,
                                    ),
                                  },
                                }
                              : pre,
                          )
                        }
                      />
                    )}
                  />
                )}
              </div>
            </StyledForm>
          </Modal>
        );
      }}
    </Formik>
  );
}

function KeyedEditModal<T extends Value>(props: EditModalProps<T>) {
  const { scenarioType, platform, region, replyOption } = props;

  const key = `${scenarioType}${platform}${region}${replyOption}`;

  return (
    <StoreContextProvider>
      <ItemSelectProvider>
        <EditModal key={key} {...props} />
      </ItemSelectProvider>
    </StoreContextProvider>
  );
}

export default KeyedEditModal;
